import { supabase } from '../lib/supabase';

// bookingDate is DD-MM-YYYY → returns DDMMYY
const datePart = (ddmmyyyy) => {
  if (!ddmmyyyy) return '000000';
  const [dd, mm, yyyy] = ddmmyyyy.split('-');
  return `${dd}${mm}${(yyyy || '').slice(2)}`;
};

// Returns the next sequence number for a given cruise_code + date suffix
// by scanning existing reference_number values that match the pattern.
async function nextSequence(cruiseCode, suffix) {
  const { data } = await supabase
    .from('booking_references')
    .select('reference_number')
    .like('reference_number', `${cruiseCode}-%-${suffix}`);
  const maxSeq = (data || []).reduce((max, r) => {
    const parts = (r.reference_number || '').split('-');
    const seq = parseInt(parts[1], 10) || 0;
    return Math.max(max, seq);
  }, 0);
  return maxSeq + 1;
}

export async function generateBookingReference(bookingId, cruiseCode, bookingDate) {
  if (!bookingId || !cruiseCode || !bookingDate) return null;

  // Check if one already exists
  const { data: existing } = await supabase
    .from('booking_references')
    .select('reference_number')
    .eq('booking_id', bookingId)
    .single();
  if (existing?.reference_number) return existing.reference_number;

  const suffix = datePart(bookingDate);
  const seq = await nextSequence(cruiseCode, suffix);
  const reference_number = `${cruiseCode}-${String(seq).padStart(3, '0')}-${suffix}`;

  const { data, error } = await supabase
    .from('booking_references')
    .insert({ booking_id: bookingId, reference_number })
    .select('reference_number')
    .single();

  if (!error && data) return data.reference_number;
  console.error('Failed to generate reference:', error);
  return null;
}

export async function getBookingReference(bookingId) {
  const { data } = await supabase
    .from('booking_references')
    .select('reference_number')
    .eq('booking_id', bookingId)
    .single();
  return data?.reference_number || null;
}

export async function getBookingReferences(bookingIds) {
  if (!bookingIds?.length) return {};
  const { data } = await supabase
    .from('booking_references')
    .select('booking_id, reference_number')
    .in('booking_id', bookingIds);
  const map = {};
  (data || []).forEach(r => { map[r.booking_id] = r.reference_number; });
  return map;
}

// Backfill references for all bookings that don't have one yet.
// Returns { inserted, skipped, errors } counts.
export async function backfillBookingReferences(onProgress) {
  // 1. Fetch all bookings ordered by date then created_at
  const { data: allBookings, error: bErr } = await supabase
    .from('bookings')
    .select('id, cruise_type, date, created_at')
    .order('date').order('created_at');
  if (bErr || !allBookings?.length) return { inserted: 0, skipped: 0, errors: 0, total: 0 };

  // 2. Fetch all existing references
  const { data: existing } = await supabase
    .from('booking_references')
    .select('booking_id, reference_number');

  const existingByBooking = new Set((existing || []).map(r => r.booking_id));

  // Build max-sequence per (cruise_code, suffix) from existing data
  const maxSeqs = {};
  (existing || []).forEach(r => {
    const parts = (r.reference_number || '').split('-');
    if (parts.length >= 3) {
      const key = `${parts[0]}||${parts[2]}`;
      maxSeqs[key] = Math.max(maxSeqs[key] || 0, parseInt(parts[1], 10) || 0);
    }
  });

  // 3. Fetch cruise code lookup
  const { data: cruiseData } = await supabase
    .from('cruise_settings')
    .select('cruise_name, cruise_code');
  const codeMap = {};
  (cruiseData || []).forEach(c => { codeMap[c.cruise_name] = c.cruise_code; });

  // 4. Build list of records to insert
  const missing = allBookings.filter(b => !existingByBooking.has(b.id));
  if (!missing.length) return { inserted: 0, skipped: 0, errors: 0, total: 0 };

  const nextSeqs = { ...maxSeqs };
  const toInsert = [];
  let skipped = 0;

  for (const booking of missing) {
    const cruiseCode = codeMap[booking.cruise_type];
    if (!cruiseCode) { skipped++; continue; }
    const suffix = datePart(booking.date);
    const key = `${cruiseCode}||${suffix}`;
    const seq = (nextSeqs[key] || 0) + 1;
    nextSeqs[key] = seq;
    const reference_number = `${cruiseCode}-${String(seq).padStart(3, '0')}-${suffix}`;
    toInsert.push({ booking_id: booking.id, reference_number });
  }

  // 5. Insert in batches of 50
  let inserted = 0, errors = 0;
  const BATCH = 50;
  for (let i = 0; i < toInsert.length; i += BATCH) {
    const batch = toInsert.slice(i, i + BATCH);
    const { error } = await supabase.from('booking_references').insert(batch);
    if (error) {
      console.error('Backfill batch error:', error);
      errors += batch.length;
    } else {
      inserted += batch.length;
    }
    if (onProgress) onProgress({ inserted, errors, total: toInsert.length });
  }

  return { inserted, skipped, errors, total: missing.length };
}
